<template>
  <div id="app">
    <header class="site-header">
      <div class="container header-inner">
        <router-link to="/" class="brand" @click="menuOpen = false">
          <img src="@/assets/emblem-256.png" alt="Hind Kranti Sena Foundation emblem" class="brand-emblem" />
          <span class="brand-text">
            <strong>{{ $t('brand.name') }}</strong>
            <small>{{ $t('brand.sub') }}</small>
          </span>
        </router-link>

        <div class="header-right">
          <div class="lang-switch" role="group" aria-label="Language">
            <button type="button" :class="{ active: $i18n.locale === 'en' }" @click="changeLocale('en')">EN</button>
            <button type="button" :class="{ active: $i18n.locale === 'hi' }" @click="changeLocale('hi')">हिं</button>
          </div>

          <button class="menu-toggle" :class="{ open: menuOpen }" @click="menuOpen = !menuOpen" aria-label="Toggle menu">
            <span></span><span></span><span></span>
          </button>

          <nav class="main-nav" :class="{ open: menuOpen }">
            <ul>
              <li><router-link to="/" @click="menuOpen = false">{{ $t('nav.home') }}</router-link></li>
              <li><router-link to="/about" @click="menuOpen = false">{{ $t('nav.about') }}</router-link></li>
              <li><router-link to="/programs" @click="menuOpen = false">{{ $t('nav.programs') }}</router-link></li>
              <li><router-link to="/gallery" @click="menuOpen = false">{{ $t('nav.gallery') }}</router-link></li>
              <li><router-link to="/news" @click="menuOpen = false">{{ $t('nav.news') }}</router-link></li>
              <li><router-link to="/contact" @click="menuOpen = false">{{ $t('nav.contact') }}</router-link></li>
            </ul>
            <router-link to="/donate" class="btn btn-accent nav-donate" @click="menuOpen = false">{{ $t('nav.donate') }}</router-link>
          </nav>
        </div>
      </div>
    </header>

    <main>
      <router-view></router-view>
    </main>

    <footer class="site-footer">
      <div class="container footer-grid">
        <div class="footer-about">
          <div class="brand">
            <img src="@/assets/emblem-256.png" alt="Hind Kranti Sena Foundation emblem" class="brand-emblem" />
            <span class="brand-text light">
              <strong>{{ $t('brand.name') }}</strong>
              <small>{{ $t('brand.sub') }}</small>
            </span>
          </div>
          <p>{{ $t('footer.about') }}</p>
        </div>

        <div class="footer-links">
          <h4>{{ $t('footer.quickLinks') }}</h4>
          <ul>
            <li><router-link to="/about">{{ $t('nav.about') }}</router-link></li>
            <li><router-link to="/programs">{{ $t('nav.programs') }}</router-link></li>
            <li><router-link to="/gallery">{{ $t('nav.gallery') }}</router-link></li>
            <li><router-link to="/news">{{ $t('nav.news') }}</router-link></li>
          </ul>
        </div>

        <div class="footer-links">
          <h4>{{ $t('footer.focusAreas') }}</h4>
          <ul>
            <li><router-link to="/programs">{{ $t('home.pillars.women.title') }}</router-link></li>
            <li><router-link to="/programs">{{ $t('home.pillars.animals.title') }}</router-link></li>
            <li><router-link to="/programs">{{ $t('home.pillars.environment.title') }}</router-link></li>
            <li><router-link to="/programs">{{ $t('home.pillars.clean.title') }}</router-link></li>
          </ul>
        </div>

        <div class="footer-contact">
          <h4>{{ $t('footer.getInTouch') }}</h4>
          <ul>
            <li>{{ $t('footer.address') }}</li>
            <li><a href="tel:+919826033529">+91-9826033529</a></li>
            <li><a href="mailto:info@hindkrantisenafoundation.org">info@hindkrantisenafoundation.org</a></li>
          </ul>
          <router-link to="/donate" class="btn btn-accent" style="margin-top:14px;">{{ $t('nav.donate') }}</router-link>
        </div>
      </div>

      <div class="footer-bottom">
        <p>&copy; {{ year }} {{ $t('footer.copyright') }} | <router-link to="/contact">{{ $t('footer.privacy') }}</router-link></p>
      </div>
    </footer>
  </div>
</template>

<script>
import { setLocale } from '@/i18n';

export default {
  name: 'App',
  data() {
    return {
      menuOpen: false,
      year: new Date().getFullYear(),
    };
  },
  watch: {
    $route() {
      this.menuOpen = false;
    },
  },
  methods: {
    changeLocale(locale) {
      setLocale(locale);
      this.menuOpen = false;
    },
  },
};
</script>

<style>
/* ---------- Design tokens ---------- */
:root {
  /* Charcoal Gray, Teal & Warm Yellow — structured palette for a
     multi-cause NGO: neutral charcoal + white carries the layout, teal
     is the secondary graphic colour, yellow is reserved for the donate
     CTA and progress/highlight accents. */
  --charcoal: #2B2E33;
  --charcoal-dark: #1B1D21;
  --charcoal-tint: #E9EAEC;
  --brand-teal: #0F7C82;
  --brand-teal-dark: #0B5C61;
  --brand-teal-tint: #E5F3F3;
  --brand-yellow: #F2B705;
  --brand-yellow-dark: #D69E00;
  --ink: #2B2E33;
  --muted: #666B70;
  --bg: #F6F6F7;
  --card: #FFFFFF;
  --border: #E3E4E6;
  --radius: 14px;
  --shadow: 0 10px 30px rgba(30, 32, 34, 0.08);
  --font-head: 'Poppins', 'Segoe UI', Arial, sans-serif;
  --font-body: 'Inter', 'Segoe UI', Arial, sans-serif;
}

* { box-sizing: border-box; }
html { scroll-behavior: smooth; }
body { margin: 0; background: var(--bg); }

#app {
  font-family: var(--font-body);
  color: var(--ink);
  line-height: 1.6;
}
h1, h2, h3, h4, .brand-text { font-family: var(--font-head); font-weight: 600; margin: 0; }

.container { max-width: 1180px; margin: 0 auto; padding: 0 24px; }
.section { padding: 72px 0; }
.section-alt { background: var(--brand-teal-tint); }
.section-head { text-align: center; max-width: 680px; margin: 0 auto 44px; }
.eyebrow {
  display: inline-block; text-transform: uppercase; letter-spacing: 1.5px;
  font-size: 0.78rem; font-weight: 700; color: var(--brand-teal); margin-bottom: 10px;
}
.section-head h2 { font-size: clamp(1.6rem, 3vw, 2.1rem); color: var(--charcoal); }
.section-head p { color: var(--muted); margin-top: 12px; }

/* ---------- Buttons ---------- */
.btn {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: var(--font-head); font-weight: 600; font-size: 0.95rem;
  padding: 13px 26px; border-radius: 999px; border: 2px solid transparent;
  text-decoration: none; cursor: pointer; transition: transform .15s ease, box-shadow .15s ease, background .15s ease;
}
.btn:hover { transform: translateY(-2px); }
.btn-accent { background: var(--brand-yellow); color: var(--charcoal-dark); box-shadow: 0 8px 20px rgba(242,183,5,0.35); }
.btn-accent:hover { background: var(--brand-yellow-dark); }
.btn-primary { background: var(--brand-teal); color: #fff; box-shadow: 0 8px 20px rgba(15,124,130,0.28); }
.btn-primary:hover { background: var(--brand-teal-dark); }
.btn-outline { background: transparent; border-color: rgba(255,255,255,0.7); color: #fff; }
.btn-outline:hover { background: rgba(255,255,255,0.12); }
.btn-outline-teal { background: transparent; border-color: var(--brand-teal); color: var(--brand-teal); }
.btn-outline-teal:hover { background: var(--brand-teal); color: #fff; }

/* ---------- Cards & grids ---------- */
.card {
  background: var(--card); border-radius: var(--radius); box-shadow: var(--shadow);
  padding: 30px 26px; border: 1px solid var(--border);
}
.grid { display: grid; gap: 26px; }
.grid-2 { grid-template-columns: repeat(2, 1fr); }
.grid-3 { grid-template-columns: repeat(3, 1fr); }
.grid-4 { grid-template-columns: repeat(4, 1fr); }
@media (max-width: 960px) { .grid-3, .grid-4 { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .grid-2, .grid-3, .grid-4 { grid-template-columns: 1fr; } }

.stat-number {
  font-family: var(--font-head); font-size: clamp(1.8rem, 4vw, 2.5rem); font-weight: 700; color: var(--charcoal);
  display: inline-block; padding-bottom: 8px; border-bottom: 3px solid var(--brand-yellow);
}
.stat-label { color: var(--muted); font-size: 0.92rem; margin-top: 8px; }

/* ---------- Header ---------- */
.site-header { position: sticky; top: 0; z-index: 50; background: #fff; box-shadow: 0 2px 14px rgba(0,0,0,0.06); }
.header-inner { display: flex; align-items: center; justify-content: space-between; padding: 12px 24px; }
.brand { display: flex; align-items: center; gap: 12px; text-decoration: none; }
.brand-emblem { width: 46px; height: 46px; object-fit: contain; border-radius: 50%; }
.brand-text { display: flex; flex-direction: column; line-height: 1.15; color: var(--charcoal); }
.brand-text strong { font-size: 1.05rem; }
.brand-text small { font-size: 0.72rem; letter-spacing: 2px; text-transform: uppercase; color: var(--brand-teal); }
.brand-text.light, .brand-text.light strong { color: #fff; }

.header-right { display: flex; align-items: center; gap: 18px; }

.lang-switch { display: flex; align-items: center; background: var(--brand-teal-tint); border-radius: 999px; padding: 3px; gap: 2px; }
.lang-switch button {
  border: none; background: transparent; font-family: var(--font-head); font-weight: 600; font-size: 0.78rem;
  padding: 6px 12px; border-radius: 999px; cursor: pointer; color: var(--brand-teal-dark); opacity: 0.6;
}
.lang-switch button.active { background: var(--brand-teal); color: #fff; opacity: 1; }

.main-nav { display: flex; align-items: center; gap: 22px; }
.main-nav ul { list-style: none; display: flex; gap: 4px; margin: 0; padding: 0; }
.main-nav a {
  color: var(--ink); text-decoration: none; font-weight: 600; font-size: 0.94rem;
  padding: 10px 14px; border-radius: 8px; transition: color .15s, background .15s;
}
.main-nav a:hover, .main-nav a.router-link-exact-active { color: var(--brand-teal); background: var(--brand-teal-tint); }
.nav-donate { padding: 10px 22px; }

.menu-toggle { display: none; flex-direction: column; gap: 5px; background: none; border: none; cursor: pointer; padding: 6px; }
.menu-toggle span { width: 24px; height: 2px; background: var(--charcoal); transition: transform .2s, opacity .2s; }

@media (max-width: 860px) {
  .menu-toggle { display: flex; }
  .main-nav {
    display: none;
    position: absolute; top: 100%; left: 0; right: 0; background: #fff;
    flex-direction: column; align-items: stretch; gap: 0; padding: 10px 24px 20px;
    box-shadow: 0 12px 20px rgba(0,0,0,0.08);
  }
  .main-nav.open { display: flex; }
  .main-nav ul { flex-direction: column; }
  .nav-donate { margin-top: 10px; align-self: flex-start; }
}

/* ---------- Footer ---------- */
.site-footer { background: var(--charcoal-dark); color: rgba(255,255,255,0.85); margin-top: 60px; }
.footer-grid { display: grid; grid-template-columns: 1.6fr 1fr 1fr 1fr; gap: 34px; padding: 56px 24px 36px; }
.footer-about p { margin-top: 14px; font-size: 0.92rem; color: rgba(255,255,255,0.75); }
.footer-links h4, .footer-contact h4 { color: #fff; font-size: 0.95rem; margin-bottom: 14px; }
.footer-links ul, .footer-contact ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; font-size: 0.9rem; }
.footer-links a { color: rgba(255,255,255,0.75); text-decoration: none; }
.footer-links a:hover { color: #fff; }
.footer-contact ul { color: rgba(255,255,255,0.75); }
.footer-bottom { border-top: 1px solid rgba(255,255,255,0.15); text-align: center; padding: 18px; font-size: 0.82rem; }
.footer-bottom a { color: rgba(255,255,255,0.85); }
@media (max-width: 860px) { .footer-grid { grid-template-columns: 1fr 1fr; } }
@media (max-width: 560px) { .footer-grid { grid-template-columns: 1fr; } }
</style>
